import React, { useEffect } from 'react';
import './BasicInformation.scss';
import { useFormikContext, Field } from 'formik';
import { FormControlLabel, FormControl, RadioGroup } from '@material-ui/core';
import RadioThemed from '../Radio';
import Switch from '@material-ui/core/Switch';
import { FormikField, FormikPhoneField } from '../../../../components/Fields';
import { EventCard } from '../Cards/EventCard';
import { VenueAgreement } from '../VenueAgreement';
import { VenueMap } from '../VenueMap/VenueMapContainer';
import { HeadingFour } from 'Components/Headings';
import { BlueTooltip } from 'src/containers/Order/Admin/Edit/orderHistoryHelpers';
import InfoIcon from '@material-ui/icons/Info';
import styled from 'styled-components';

export const BasicInformation = () => {
  const {
    setFieldValue,
    values: { renterGroupCodeMode, allowDefferedEnabled, allowAddOnOnlyOrder, protectedEvent, hidden }
  } = useFormikContext();

  useEffect(() => {
    if (!allowDefferedEnabled) {
      setFieldValue('renterGroupCodeMode', '');
    }
  }, [allowDefferedEnabled]);

  useEffect(() => {
    if (!allowAddOnOnlyOrder) {
      setFieldValue('allowAddOnOnlyOrder', false);
    }
  }, [allowAddOnOnlyOrder]);

  useEffect(() => {
    if (!protectedEvent) {
      setFieldValue('password', '');
    }
  }, [protectedEvent]);

  useEffect(() => {
    if (!hidden) {
      setFieldValue('hidden', false);
    }
  }, [hidden]);

  const groupCodeChangeHandler = event => {
    const { value } = event.target;
    setFieldValue('renterGroupCodeMode', value);
  };

  return (
    <EventCard title={'Basic Information'} testId="basic-information">
      <div className={'card-row'}>
        <div className={'card-col'}>
          <HeadingFour label="Event Name" className="" />
          <Field component={FormikField} label="Event Name" name="eventName" type="text" variant="filled" />
        </div>
        <div className={'card-col right-col'}>
          <HeadingFour label="Check In and Check Out" className="" />
          <div className={'card-inner-row'}>
            <div className={'card-span'}>
              <div className={'time-picker'}>
                <Field component={FormikField} label="Check In Time" type="time" name="checkInTime" variant="filled" />
              </div>
            </div>

            <div className={'card-span'}>
              <div className={'time-picker'}>
                <Field component={FormikField} label="Check Out Time" type="time" name="checkOutTime" variant="filled" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={'card-row separator'}>
        <div className={'card-col description'}>
          <div className={'optional-text'}>
            <HeadingFour label="Event Description" className="" />
            <div>Optional</div>
          </div>
          <Field component={FormikField} label="Event Description" name="eventDescription" type="text" variant="filled" rows={'4'} multiline />
        </div>
        <div className="file-uploads">
          <div className={'card-col venue-agreement right-col'}>
            <HeadingFour label="Venue Agreement" className="" />
            <VenueAgreement />
          </div>
          <div className={'card-col venue-agreement right-col'}>
            <HeadingFour label="Venue Map" className="" />
            <VenueMap />
          </div>
        </div>
      </div>

      <div className={'card-row separator'}>
        <ContactPhone>
          <Field name="phone" autoComplete="none">
            {({ field, meta }) => (
              <FormikPhoneField
                {...field}
                {...meta}
                label="Contact Phone for Event"
                autoComplete="phone"
                variant="filled"
                onChange={e => setFieldValue('phone', e.target.value.replace(/[^0-9]/g, ''))}
              />
            )}
          </Field>
          <BlueTooltip
            placement="top"
            arrow
            title={
              <p
                style={{
                  fontSize: '14px',
                  textAlign: 'center',
                  margin: '5px 0'
                }}>
                Optional. If not specified, the venue's phone number will be displayed by default.
              </p>
            }>
            <InfoIcon fontSize="small" />
          </BlueTooltip>
        </ContactPhone>
      </div>

      <div className={'card-row separator'}>
        <div className="basic-info__deferred">
          <div className="basic-info__deffered--header">
            <HeadingFour label="Allow Add-On only orders" className="" />
            <div data-testid="add-on-enabled">
              <Switch
                onChange={() => setFieldValue('allowAddOnOnlyOrder', !allowAddOnOnlyOrder)}
                size={'medium'}
                checked={allowAddOnOnlyOrder}
                className="deferred-enable"
                color="primary"
              />
            </div>
            <BlueTooltip
              placement="top"
              arrow
              title={
                <p
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: '5px 0'
                  }}>
                  Turning on will allow renters to create add-On only orders
                </p>
              }>
              <InfoIcon fontSize="small" />
            </BlueTooltip>
          </div>
        </div>
      </div>

      <div className={'card-row separator'}>
        <div className="basic-info__deferred">
          <div className="basic-info__deffered--header">
            <HeadingFour label="Allow Renters to Delay their Payment" className="" />
            <div data-testid="deferred-enabled">
              <Switch
                onChange={() => setFieldValue('allowDefferedEnabled', !allowDefferedEnabled)}
                size={'medium'}
                checked={allowDefferedEnabled}
                className="deferred-enable"
                color="primary"
              />
            </div>
            <BlueTooltip
              placement="top"
              arrow
              title={
                <p
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: '5px 0'
                  }}>
                  Turning on will add the group to deferred payment options
                </p>
              }>
              <InfoIcon fontSize="small" />
            </BlueTooltip>
          </div>
          {allowDefferedEnabled && (
            <>
              <p className="deferred-description">Choose the type of delayed payment you would like to offer.</p>
              <FormControl component="fieldset" className="deferred-fieldset">
                <RadioGroup name="renterGroupCodeMode" value={renterGroupCodeMode} onChange={groupCodeChangeHandler}>
                  <FormControlLabel value="unsecured" control={<RadioThemed />} label="Allow delayed payment with a credit/debit card on file" />
                  <FormControlLabel
                    value="secured"
                    control={<RadioThemed color={'secondary'} />}
                    label={
                      <div className="deferred__access-label--container">
                        <span className="deferred__access-label--label">Allow delayed payment with an access code</span>
                        <BlueTooltip
                          placement="top"
                          arrow
                          title={
                            <p
                              style={{
                                fontSize: '14px',
                                textAlign: 'center',
                                margin: '5px 0'
                              }}>
                              This feature enables select groups to delay their payment with an access code
                            </p>
                          }>
                          <InfoIcon fontSize="small" />
                        </BlueTooltip>
                      </div>
                    }
                  />
                </RadioGroup>
              </FormControl>
              {renterGroupCodeMode.length > 0 && (
                <div className="deferred-selection-notice">
                  <InfoIcon fontSize="large" style={{ paddingRight: '5px' }} />
                  <span>
                    <strong>Disclaimer</strong>: By selecting this option, you understand that you are accepting liability and responsibility for paying
                    platform fees that occur from payments not collected at the end of the event.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className={'card-row separator'}>
        <div className="basic-info__hide-event">
          <div className="basic-info__hide-event--header">
            <HeadingFour label="Hide this Event from the Events Page" className="" />
            <div>
              <Switch onChange={() => setFieldValue('hidden', !hidden)} size={'medium'} checked={hidden} className="hide-event-enable" color="primary" />
            </div>
            <BlueTooltip
              placement="top"
              arrow
              title={
                <p
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: '5px 0'
                  }}>
                  The event will be visible only for admins.
                </p>
              }>
              <InfoIcon fontSize="small" />
            </BlueTooltip>
          </div>
        </div>
      </div>

      <div className={'card-row separator'}>
        <div className="basic-info__protected-event">
          <div className="basic-info__protected-event--header">
            <HeadingFour label="Password Protected" className="" />
            <div>
              <Switch
                onChange={() => setFieldValue('protectedEvent', !protectedEvent)}
                size={'medium'}
                checked={protectedEvent}
                className="protected-event-enable"
                color="primary"
              />
            </div>
            <BlueTooltip
              placement="top"
              arrow
              title={
                <p
                  style={{
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: '5px 0'
                  }}>
                  Protect your event with a password to give to VIP renters. Change or remove password at any time.
                </p>
              }>
              <InfoIcon fontSize="small" />
            </BlueTooltip>
          </div>
        </div>
      </div>

      <div className={'card-row separator'}>
        {protectedEvent && (
          <div className={'card-row separator'}>
            <div className="basic-info__password">
              <div className={'card-col'}>
                <Field component={FormikField} label="Password" name="password" type="text" variant="filled" />
              </div>
            </div>
          </div>
        )}
      </div>
    </EventCard>
  );
};

const ContactPhone = styled.div`
  width: 100%;
  max-width: 300px;
  display: flex;
  align-items: center;
  gap: 10px;

  .event-phone-field {
    padding: 0;
    margin: 0;
  }
`;
